import { toggleFieldAttributeV2 as toggleFieldAttribute } from "commonAssets/lib/toggleFieldAttributeV2";
import { toggleForeignPhone } from "commonAssets/lib/toggleForeignPhone.js";
import { decodeItemId, itemIdOf } from "./utils";
import {
    cleanLabel,
    layoutFromDom,
    parsePrefixedName,
    remapLegacyEntries,
} from "./legacyItemIds";

/**
 * The item-id of the first (always-rendered) row of a transaction table.
 * Mirrors the scheme in customSubmitClaim.js (see utils.js `itemIdOf`).
 *
 * @param {number} section
 * @param {number} table
 * @returns {number}
 */
function baseItemId(section, table) {
    return itemIdOf(section, table, 1);
}

/**
 * Ensure the transaction row for the given item-id exists in the DOM, adding
 * rows via the table's "Add" button as needed.
 *
 * @param {number|string} itemId
 * @returns {boolean} True if the row exists (or was created).
 */
function ensureTransactionRow(itemId) {
    if (document.querySelector(`[item-id="${itemId}"]`)) return true;

    if (isNaN(parseInt(itemId, 10))) return false;

    // The first row of every table is always rendered
    // (e.g. 120000002 -> 120000001).
    const { section, table: tableNumber, row } = decodeItemId(itemId);
    const firstRowId = baseItemId(section, tableNumber);
    const anchor = document.querySelector(`[item-id="${firstRowId}"]`);
    if (!anchor) return false;

    const table = anchor.closest("table");
    if (!table) return false;

    const tableID = table.id.split("_")[1];
    const addBtn = document.getElementById("add_transactions_" + tableID);
    if (!addBtn) return false;

    // Click "Add" until the target row exists (guarded against runaway loops).
    let guard = 0;
    while (!document.querySelector(`[item-id="${itemId}"]`) && guard < row) {
        addBtn.click();
        guard++;
    }

    return Boolean(document.querySelector(`[item-id="${itemId}"]`));
}

/**
 * Find a transaction input within a given row (item-id) whose cleaned
 * data-label matches. For radios, the value must also match.
 *
 * @param {number|string} itemId
 * @param {string} cleanedLabel
 * @param {string} [value]
 * @returns {HTMLElement|null}
 */
function findTransactionInput(itemId, cleanedLabel, value) {
    const candidates = document.querySelectorAll(`[item-id="${itemId}"]`);
    for (const el of candidates) {
        if (cleanLabel(el.getAttribute("data-label")) !== cleanedLabel) continue;
        if (el.type === "radio" && value !== undefined && el.value !== value) {
            continue;
        }
        return el;
    }
    return null;
}

/**
 * Set a value on an input based on its type.
 *
 * @param {HTMLElement} input
 * @param {*} value
 */
function setInputValue(input, value) {
    switch (input.type) {
        case "text":
        case "tel":
        case "email":
        case "number":
        case "date":
        case "select-one":
        case "hidden":
            input.value = value;
            break;
        case "textarea":
            input.innerText = value;
            break;
        case "checkbox":
            input.checked = String(value).toUpperCase() === "YES";
            break;
        case "radio":
            input.checked = true;
            break;
    }
}

/**
 * @param {*} member
 * @param {*} config
 */
export default function loadMember(member, config) {
    const claimFields = member.claim_fields || [];
    const claimTransactions = member.claim_transactions || [];
    const claimFiles = member.claim_files || [];

    // Transaction values: prefixed claim_fields (e.g. "B.1: TOTAL NUMBER OF
    // SHARES", stored without an item-id) and claim_transactions. Values saved
    // under legacy item-ids are re-homed into their true table and row, so a
    // resubmission stores them correctly (see legacyItemIds.js).
    const transactionEntries = [
        ...claimFields
            .filter((field) => parsePrefixedName(field.name))
            .map((field) => ({ ...field, source: "field" })),
        ...claimTransactions.map((t) => ({ ...t, source: "transaction" })),
    ];
    const { entries, missing } = remapLegacyEntries(
        transactionEntries,
        layoutFromDom(document)
    );

    const moved = entries.filter((e) => e.moved);
    if (moved.length) {
        console.warn(
            `[loadMember] Re-homed ${moved.length} value(s) saved under the wrong table:`,
            moved.map((e) => `${e.legacy_item_id} → ${e.item_id} ${e.name}`)
        );
    }

    for (const entry of entries) {
        const itemId = entry.item_id;
        const parsed = parsePrefixedName(entry.name);
        const cleaned = cleanLabel(parsed ? parsed.label : entry.name);

        if (!ensureTransactionRow(itemId)) {
            console.warn(
                `[loadMember] No table row for transaction "${entry.name}" (item-id ${itemId}); skipping.`
            );
            continue;
        }

        // requestAnimationFrame ensures newly added rows are available.
        requestAnimationFrame(() => {
            const input = findTransactionInput(itemId, cleaned, entry.value);
            if (input) setInputValue(input, entry.value);
        });
    }

    // Choice fields lost to a legacy id collision (e.g. a purchase's
    // Transaction Type overwritten by the sale's): flag them for re-entry.
    if (missing.length) {
        console.warn(
            `[loadMember] ${missing.length} value(s) were lost on the original submission and must be re-entered:`,
            missing.map((m) => `${m.item_id} ${m.name}`)
        );
        requestAnimationFrame(() => {
            for (const m of missing) {
                const input = findTransactionInput(
                    m.item_id,
                    cleanLabel(parsePrefixedName(m.name).label)
                );
                if (input) input.classList.add("is-invalid");
            }
        });
    }

    for (const field of claimFields) {
        if (parsePrefixedName(field.name)) continue;

        // Regular claim field: match by raw data-label. Target actual form
        // controls only — some fields (e.g. the backup-withholding checkbox)
        // also have a wrapper <div> carrying the same data-label.
        const fieldSelector = `input[data-label="${field.name}"], select[data-label="${field.name}"], textarea[data-label="${field.name}"]`;
        let input = document.querySelector(fieldSelector);
        if (!input) continue;

        if (input.type === "radio") {
            input = document.querySelector(
                `input[data-label="${field.name}"][value="${field.value}"]`
            );
            if (input) input.checked = true;
        } else {
            setInputValue(input, field.value);
        }
    }

    // Handle claim files for multiple documentation sections
    if (claimFiles.length > 0) {
        // Group files by their documentation section (doc_id)
        const filesBySection = {};

        for (const file of claimFiles) {
            const docId = getDocId(config, file.form_id);
            if (!filesBySection[docId]) {
                filesBySection[docId] = [];
            }
            filesBySection[docId].push(file);
        }

        // Process each documentation section
        for (const [docId, files] of Object.entries(filesBySection)) {
            const list = document.getElementById(`file-list-${docId}`);
            const noFilesElement = document.getElementById(`no-files-${docId}`);

            if (list && files.length > 0) {
                list.classList.remove("d-none");
                if (noFilesElement) {
                    noFilesElement.classList.add("d-none");
                }

                for (const file of files) {
                    const li = document.createElement("li");
                    li.setAttribute("disabled", "disabled");
                    li.innerHTML = `<span class="float-right badge ${file.failed ? "bg-danger" : "bg-success"}">${file.failed ? "FAILED" : "Uploaded"}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                    list.appendChild(li);
                }
            }
        }

        // Fallback for legacy single file-list support
        const legacyList = document.getElementById("file-list");
        const legacyNoFiles = document.getElementById("no-files");

        if (legacyList && claimFiles.length > 0) {
            legacyList.classList.remove("d-none");
            if (legacyNoFiles) {
                legacyNoFiles.classList.add("d-none");
            }

            for (const file of claimFiles) {
                const li = document.createElement("li");
                li.setAttribute("disabled", "disabled");
                li.innerHTML = `<span class="float-right badge ${file.failed ? "bg-danger" : "bg-success"}">${file.failed ? "FAILED" : "Uploaded"}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
                legacyList.appendChild(li);
            }
        }
    }

    for (const key in member) {
        if (!member.hasOwnProperty(key)) continue;

        const field = document.getElementById(key);
        if (!field) continue;

        field.value = member[key];
    }

    const country = document.getElementById("country");
    if (country.value === "") country.value = "USA";
    if (country.value !== "USA") {
        document.querySelectorAll("#state,#zip_code").forEach((input) => {
            toggleFieldAttribute(input, "required", false);
            input.parentNode.classList.add("d-none");
        });
        document.querySelectorAll("#province,#postal_code").forEach((input) => {
            toggleFieldAttribute(input, "required", true);
            input.parentNode.classList.remove("d-none");
        });
        toggleForeignPhone(country.value);
    }

    // if (member.claim_id) $('#confirm_email_address').val($('#email_address').val());
    if (member.claim_id)
        document.getElementById("confirm_email_address").value =
            document.getElementById("email_address").value;
}

function getDocId(config, label) {
    // return the config key whose data_file_label matches the label
    for (const key in config) {
        if (config[key].data_file_label === label) {
            return key;
        }
    }
    return "documentation";
}
